import {
  AsyncPipe,
  NgFor,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  TitleCasePipe
} from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { format, parse } from 'date-fns';
import moment from 'moment';
import {
  NgxMatTimepickerComponent,
  NgxMatTimepickerModule
} from 'ngx-mat-timepicker';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, filter, map, startWith, tap } from 'rxjs/operators';
import { NEW_TRANSACTION_DIALOG } from '../../../../testIds';
import { BookingStatus } from '../../../models/booking-status';
import { Customer } from '../../../models/customer';
import { TestIdDirective } from '../../directives/test-id.directive';
import { BookingItemNamePipe } from '../../pipes/booking-item-name.pipe';
import { CustomerAddressPipe } from '../../pipes/customer-address.pipe';
import { CustomerNamePipe } from '../../pipes/customer-name.pipe';
import { DeliveryItemNamePipe } from '../../pipes/delivery-item-name.pipe';
import { BookingService } from '../../services/booking.service';
import { ConfigService } from '../../services/config.service';
import { CustomerService } from '../../services/customer.service';
import { DocketService } from '../../services/docket.service';
import { RepairDocketService } from '../../services/repair-docket.service';
import { SaleDocketService } from '../../services/sale-docket.service';
import { CustomerAlertDialogComponent } from '../customer-alert-dialog/customer-alert-dialog.component';
import { NewCustomerDialogComponent } from '../new-customer-dialog/new-customer-dialog.component';
@Component({
  selector: 'new-hire-dialog',
  templateUrl: './new-transaction-dialog.component.html',
  styleUrls: ['./new-transaction-dialog.component.scss'],
  providers: [
    DocketService,
    CustomerService,
    BookingService,
    SaleDocketService,
    RepairDocketService,
    BookingItemNamePipe,
    DeliveryItemNamePipe
  ],
  standalone: true,
  imports: [
    MatDialogTitle,
    FlexModule,
    MatDialogContent,
    NgIf,
    MatFormField,
    TestIdDirective,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    NgFor,
    MatOption,
    NgSwitch,
    NgSwitchCase,
    MatRadioGroup,
    FormsModule,
    MatRadioButton,
    MatDivider,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatError,
    MatDatepicker,
    MatIcon,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    TitleCasePipe,
    CustomerNamePipe,
    CustomerAddressPipe,
    NgxMatTimepickerModule,
    MatSelectModule
  ]
})
export class NewTransactionDialogComponent implements OnInit {
  testIds = NEW_TRANSACTION_DIALOG;
  filteredCustomers: Customer[];
  selectedCustomer: Customer;
  searchTerm = new UntypedFormControl();

  minimumDate = moment();
  form: UntypedFormGroup;
  genericGroup: UntypedFormGroup;
  bookingGroup: UntypedFormGroup;
  loading = false;
  type: 'hire' | 'booking' | 'sale' | 'repair';
  status: BookingStatus;
  availableStatuses = BookingStatus;
  private poRequired = new BehaviorSubject<boolean>(false);
  poRequired$ = this.poRequired.asObservable();

  transactionProductType: 'retail' | 'hire' | 'repair' | null;

  currencies$ = this.configService.userConfig.pipe(
    map((config) => [
      config.currency ?? 'GBP',
      ...(config.additional_currencies?.split(',') ?? [])
    ]),
    tap((currencies) => {
      if (currencies.length > 1) {
        this.currencyFieldsSetup(currencies);
      }
    })
  );

  multipleCurrencies$ = this.currencies$.pipe(
    map((currencies) => currencies.length > 1)
  );
  defaultCurrency$ = this.configService.userConfig.pipe(
    map((config) => config.currency ?? 'GBP')
  );

  constructor(
    public dialogRef: MatDialogRef<NewTransactionDialogComponent>,
    private docketService: DocketService,
    private saleDocketService: SaleDocketService,
    private repairDocketService: RepairDocketService,
    private router: Router,
    private customerService: CustomerService,
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private bookingService: BookingService,
    private toastrService: ToastrService,
    private configService: ConfigService,
    @Inject(MAT_DIALOG_DATA) public data
  ) {
    this.genericGroup = this.formBuilder.group({
      customer_id: this.formBuilder.control(null, Validators.required),
      purchase_order: this.formBuilder.control(null),
      currency_code: this.formBuilder.control(null),
      currency_rate: this.formBuilder.control(null),
      tax_rate: this.formBuilder.control(0.2)
    });
    this.bookingGroup = this.formBuilder.group({
      customer_id: this.formBuilder.control(null, Validators.required),
      on_hire_date: this.formBuilder.control(null, Validators.required),
      on_hire_time: this.formBuilder.control(null, Validators.required),
      off_hire_date: this.formBuilder.control(null, Validators.required),
      off_hire_time: this.formBuilder.control(null, Validators.required),
      purchase_order: this.formBuilder.control(null),
      status: this.formBuilder.control(null, Validators.required)
    });
    if (data && 'customer' in data) {
      // skip popup as cstomer can only be provided from customer profile, and popup will already have been shown there
      this.selectCustomer(data['customer'], true);
    }
    this.transactionProductType = data?.transactionProductType ?? null;
    if (this.transactionProductType === 'retail') {
      this.transactionTypeChanged('sale');
    }
    if (this.transactionProductType === 'repair') {
      this.transactionTypeChanged('repair');
    }
  }

  private currencyFieldsSetup(currencies: any[]) {
    this.genericGroup.controls['currency_code'].setValidators(
      Validators.required
    );
    this.genericGroup.controls['currency_rate'].setValidators(
      Validators.required
    );
    this.genericGroup.controls['currency_rate'].setValue(1);
    this.genericGroup.controls['currency_code'].setValue(currencies[0]);
  }

  ngOnInit() {
    this.searchTerm.valueChanges
      .pipe(startWith(''), debounceTime(300))
      .subscribe((value) => {
        if (value && typeof value === 'string') {
          this.searchCustomers(value);
        } else {
          this.searchTerm.patchValue('');
        }
      });

    this.bookingGroup.controls['on_hire_date'].valueChanges.subscribe(
      (value) => {
        console.log(value?.toDate() ?? 'no date', value);
      }
    );
  }

  transactionTypeChanged(type: 'hire' | 'booking' | 'sale' | 'repair') {
    this.type = type;
    switch (type) {
      case 'hire':
      case 'sale':
      case 'repair':
        this.form = this.genericGroup;
        break;
      case 'booking':
        this.form = this.bookingGroup;
        break;
      default:
        throw Error('Unknown new transaction type');
    }
    this.form.controls['customer_id'].patchValue(this.selectedCustomer?.id);
  }

  searchCustomers(value) {
    this.customerService.search(value).subscribe((results) => {
      this.filteredCustomers = results;
    });
  }

  selectCustomer(customer: Customer, skipPopup = false) {
    if (!skipPopup) {
      this.checkForCustomerAlert(customer);
    }
    if (customer.hires_disabled) {
      this.searchTerm.patchValue('');
      this.toastrService.warning('Hires disabled for this customer', 'Warning');
      return;
    }
    this.selectedCustomer = customer;
    this.searchTerm.patchValue('');
    this.poRequired.next(Boolean(customer.extras?.require_po));
    if (this.form) {
      this.form.controls['customer_id'].patchValue(customer.id);
    }
    if (customer.extras?.require_po) {
      const poControl = this.genericGroup.get('purchase_order');
      poControl.setValidators(Validators.required);
      poControl.markAsTouched();
      const bookingPoControl = this.bookingGroup.get('purchase_order');
      bookingPoControl.setValidators(Validators.required);
      bookingPoControl.markAsTouched();
    }
  }

  private checkForCustomerAlert(customer: Customer) {
    if (customer.popup_message) {
      this.dialog.open(CustomerAlertDialogComponent, {
        data: customer
      });
    }
  }

  deselectCustomer() {
    this.selectedCustomer = null;
  }

  optionSelectedEvent($event: MatAutocompleteSelectedEvent) {
    if ($event.option.value === false) {
      this.newCustomer();
      return;
    }
    this.selectCustomer($event.option.value);
  }

  create() {
    if (!this.form.valid) {
      return;
    }
    this.loading = true;
    switch (this.type) {
      case 'hire':
        this.createDocket();
        break;
      case 'sale':
        this.createSaleDocket();
        break;
      case 'booking':
        this.createBooking();
        break;
      case 'repair':
        this.createRepairDocket();
        break;
    }
  }

  private createBooking() {
    this.bookingService
      .create(this.transformDates(this.bookingGroup.value))
      .subscribe(
        (response) => {
          const bookingId = response.id;
          this.router.navigate(['bookings/view', bookingId], {
            queryParams: { addItemId: this.data?.itemId ?? undefined }
          });
          this.dialogRef.close();
        },
        (error) => {
          this.loading = false;
          this.toastrService.error('Error creating booking', 'Error');
          throw error;
        }
      );
  }

  private createDocket() {
    this.docketService.create(this.genericGroup.value).subscribe(
      (response) => {
        const docketId = response.id;
        this.router.navigate(['docket', docketId], {
          queryParams: { addItemId: this.data?.itemId ?? undefined }
        });
        this.dialogRef.close();
      },
      (error) => {
        this.loading = false;
        this.toastrService.error('Error creating hire contract', 'Error');
        throw error;
      }
    );
  }

  private createSaleDocket() {
    this.saleDocketService
      .addSaleDocket({ ...this.genericGroup.value, account: false })
      .subscribe(
        (response) => {
          this.router.navigate(['sale-docket', response.id], {
            queryParams: { addRetailItemId: this.data?.itemId ?? undefined }
          });
          this.dialogRef.close();
        },
        (error) => {
          this.loading = false;
          this.toastrService.error('Error creating sale', 'Error');
          throw error;
        }
      );
  }

  private createRepairDocket() {
    this.repairDocketService
      .addRepairDocket({ ...this.genericGroup.value, account: false })
      .subscribe(
        (response) => {
          this.router.navigate(['repair-docket', response.id], {
            queryParams: {
              addItemId: this.data?.itemId ?? undefined,
              addItemType: this.data?.addItemType ?? undefined
            }
          });
          this.dialogRef.close();
        },
        (error) => {
          this.loading = false;
          this.toastrService.error('Error creating repair docket', 'Error');
          throw error;
        }
      );
  }

  private transformDates(properties) {
    properties['on_hire'] = parse(
      properties['on_hire_time'],
      'HH:mm',
      properties['on_hire_date'].toDate()
    );
    properties['off_hire'] = parse(
      properties['off_hire_time'],
      'HH:mm',
      properties['off_hire_date'].toDate()
    );
    delete properties['on_hire_date'];
    delete properties['on_hire_time'];
    delete properties['off_hire_date'];
    delete properties['off_hire_time'];
    return properties;
  }

  newCustomer() {
    this.dialog
      .open(NewCustomerDialogComponent, {
        minWidth: '50vw'
      })
      .afterClosed()
      .pipe(filter((customer) => !!customer))
      .subscribe((customer) => {
        this.selectCustomer(customer);
      });
  }

  updateTime(event: any, field: string) {
    this.form.get(field).patchValue(event);
  }

  updateTimeEnteredManually(field: string, picker: NgxMatTimepickerComponent) {
    const control = this.form.get(field);
    if (!control.value) {
      return;
    }
    // 24 hr patterns to try and parse
    const parsePatterns = ['HH:mm', 'HH.mm', 'H:mm', 'HHmm', 'Hmm', 'HH', 'H'];
    const invalidValue = control.value;
    for (const pattern of parsePatterns) {
      const parsed = parse(invalidValue, pattern, new Date());
      if (parsed.toString() !== 'Invalid Date') {
        control.patchValue(format(parsed, 'HH:mm'));
        return picker.updateTime(control.value);
      }
    }
    control.setErrors({ invalidTime: true });
  }
}
